import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { getGameRosterPlayers } from '@sportninja/common/services/game/attendanceServices'
import type { AttendancePlayer, RosterPlayersResponse } from '../types'

/**
 * How long the roster stays fresh — same as the Player Finder list. Short, so
 * reopening the modal shows another official's changes or a player's new
 * availability; nothing else refetches it on open, and the app-wide default
 * (common/config/queryClient) is longer.
 */
const STALE_TIME = 20 * 1000

interface Params {
  gameId?: string
  rosterId?: string
}

/**
 * Roster players + embedded spare_candidates for the Manage Attendance modal.
 * Wraps the shared gameRosterPlayers query key so mutations can invalidate it.
 *
 * `getGameRosterPlayers` already unwraps the `{ data }` envelope internally
 * (see common/services/game/attendanceServices.js), so `query.data` here is
 * the `RosterPlayersResponse` payload directly — not `{ data: ... }`.
 */
export const useGameAttendancePlayers = ({ gameId, rosterId }: Params) => {
  const query = useQuery({
    queryKey: queryKeys.gameRosterPlayers.byRoster(gameId, rosterId),
    queryFn: () =>
      getGameRosterPlayers(
        gameId as string,
        rosterId as string
      ) as Promise<RosterPlayersResponse>,
    enabled: Boolean(gameId && rosterId),
    staleTime: STALE_TIME,
  })

  const data = query.data
  return {
    players: (data?.players ?? []) as AttendancePlayer[],
    spareCandidates: (data?.spare_candidates ?? []) as AttendancePlayer[],
    teamId: data?.team_id ?? null,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : false,
    refetch: query.refetch,
  }
}
